import React, { useMemo, useState, lazy, Suspense, useCallback } from "react";
import RegistrationUserState from "../context/registeredUser/registeredUserState.js";
import {
  Dialog,
  CssBaseline,
  Container,
  Grid,
  Box,
  Paper,
  Typography,
  Slide,
} from "@material-ui/core";
import moment from "moment";
import momentTz from "moment-timezone";
import { formatEventTimeForViewer, getEventDateText, getEventTimezoneLabel, getHoldingBlockLayout } from "../helpers";
import "./index.css";
import "./RegistrationEmail.css";

// Components
import HeaderBar from "../components/header/HeaderBar";
import BackgroundImg from "../components/UI/BackgroundImg";
import RegistrationEmail from "./RegistrationEmail";

const Logo = lazy(() => import("../components/UI/Logo"));

// Custom Slide Transition for Dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const SPECIAL_HIDE_LOGO_EVENT_ID = "6617f9d68d18254c64eac445";
const SPECIAL_HICL_LOGO_EVENT_IDS = new Set([
  "69175e8f27ca94001446d2f9",
  "68f6124464cab60013721242",
]);
const SPECIAL_HICL_LOGO_URL =
  "https://brrmedia-clients-uploads.s3.us-west-2.amazonaws.com/HICL/HICL_TRIG+V2.png";

function findWebsiteLogoPath(eventItem) {
  if(eventItem?.webcast?.branding?.logoSelection) {
    console.log('logo selection', eventItem?.webcast?.branding?.logoSelection?.path);
    return eventItem?.webcast?.branding?.logoSelection?.path;
  }
  const logos = eventItem?.companies?.[0]?.logos || [];
  const websiteLogo = logos.find((l) => l?.type === "website");
  return websiteLogo?.upload?.path || null;
}

function computeBrandingFound(eventItem) {
  const fonts = eventItem?.webcast?.branding?.fonts;
  if (!fonts) return false;
  
  // Handle new structure: object with keys
  if (fonts && typeof fonts === 'object' && !Array.isArray(fonts)) {
    return !!(fonts.default?.fontFamily || fonts.title?.fontFamily || fonts.description?.fontFamily);
  }
  
  // Handle old structure: array
  if (Array.isArray(fonts) && fonts.length > 0) {
    return fonts.some(font => font?.fontLinkHref && font.fontLinkHref !== '');
  }
  
  return false;
}

function computeLogoPadding(eventItem) {
  const padding = eventItem?.webcast?.branding?.logo?.padding;
  return padding && padding !== "" ? padding : "10px";
}

function computeHeaderTitle(eventItem) {
  return eventItem?.headerTitle || eventItem?.title || "";
}

function computeHeaderBackground(eventItem) {
  const bg = eventItem?.webcast?.colour?.background;
  return bg && bg !== "" ? bg : "#ffffff";
}

function resolveLogoSrc(eventItem, logoPath) {
  if(eventItem?.webcast?.branding?.logoSelection) {
    return eventItem?.webcast?.branding?.logoSelection?.path?.startsWith('http') ? eventItem?.webcast?.branding?.logoSelection?.path : `${process.env.REACT_APP_ASSETS_BASE_URL}/${eventItem?.webcast?.branding?.logoSelection?.path}`;
  }
  if (!logoPath) return null;
  if (SPECIAL_HICL_LOGO_EVENT_IDS.has(eventItem?._id)) return SPECIAL_HICL_LOGO_URL;
  return `${process.env.REACT_APP_ASSETS_BASE_URL}/${logoPath}`;
}

function Background({ eventItem }) {
  if (eventItem?.holdingSlide?.path) {
    return <BackgroundImg path={eventItem.holdingSlide.path} img={true} colour={null} />;
  }

  const hbb = eventItem?.webcast?.branding?.holdingBlockBackground;
  if (hbb?.type) {
    const opacity = hbb.opacity != null ? hbb.opacity : 1;
    if (hbb.type === "image") {
      return hbb.image?.path
        ? <BackgroundImg path={hbb.image.path} img={true} colour={null} opacity={opacity} />
        : null;
    }
    if (hbb.type === "video") {
      return hbb.video?.path
        ? <BackgroundImg path={hbb.video.path} video={true} colour={null} opacity={opacity} />
        : null;
    }
    if (hbb.type === "color" && hbb.color) {
      return <BackgroundImg path={null} img={false} colour={hbb.color} opacity={opacity} />;
    }
    return null;
  }

  const bg = eventItem?.webcast?.colour?.background;
  if (bg !== null && bg !== undefined && bg !== "") {
    return <BackgroundImg path={null} img={false} colour={bg} />;
  }

  return null;
}

function LogoBlock({ eventItem, logoPath, logoPadding, className }) {
  const logoSrc = resolveLogoSrc(eventItem, logoPath);
  if (!logoSrc) return null;

  return (
    <Box mb={2} className={className}>
      <Suspense fallback={null}>
        <Logo
          src={logoSrc}
          title="headerTitle"
          logoPadding={logoPadding}
          backgroundColor="#fff"
        />
      </Suspense>
    </Box>
  );
}

function ZoomDialogContent({ eventItem, logoPath, logoPadding, match, event, loading }) {
  return (
    <div className="zoomRegistrationRow">
      {/* Left: Branding Section */}
      <Paper
        elevation={4}
        className="boxContainer creativePaper zoomRegistrationCol"
        style={{
          padding: "2rem",
          color: "black",
          borderRadius: "10px",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <LogoBlock
            eventItem={eventItem}
            logoPath={logoPath}
            logoPadding={logoPadding}
            className="showLogo"
          />

          <Typography className="title branding-title">{eventItem?.title}</Typography>

          <Typography
            variant="subtitle1"
            className="title branding-title-upcoming"
            style={{ marginTop: "0.5rem" }}
          >
            {getEventDateText(eventItem) || (
              <>
                {eventItem?.releaseTime && formatEventTimeForViewer(eventItem.releaseTime, `${eventItem?.dateFormat}`)}
                {eventItem?.releaseTime && !eventItem?.hideTimezoneLabel && (
                  <span> ({getEventTimezoneLabel(eventItem.releaseTime)})</span>
                )}
              </>
            )}
          </Typography>

          <Box mt={2}>
            <Typography
              style={{ fontWeight: "bold", color: "black" }}
              variant="body1"
              className="description branding-title-upcoming"
              gutterBottom
            >
              Please note, you will join via a Zoom Webinar, where you are able to watch
              the session and use the raised hand function to participate in Q&amp;A.
            </Typography>

            <Typography
              style={{ fontWeight: "bold", color: "black" }}
              variant="body1"
              className="description branding-title-upcoming"
              gutterBottom
            >
              If you choose the dial in option please add your phone number.
              Unrecognised numbers will be removed from the call.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Right: Registration Form */}
      <div
        className="boxContainer creativePaper zoomRegistrationCol"
        style={{
          padding: "2rem",
          background: "#ffffff",
          borderRadius: "10px",
          boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
        }}
      >
        <LogoBlock
          eventItem={eventItem}
          logoPath={logoPath}
          logoPadding={logoPadding}
          className="showLogoReverse"
        />

        <RegistrationUserState>
          <RegistrationEmail match={match} event={event} saveAuth={false} loading={loading} />
        </RegistrationUserState>
      </div>
    </div>
  );
}

function DefaultDialogContent({ eventItem, logoPath, logoPadding, match, event, loading }) {
  const shouldShowTopLogo = eventItem?._id !== SPECIAL_HIDE_LOGO_EVENT_ID;

  return (
    <>
      {shouldShowTopLogo && (
        <Box style={{ display: "flex", justifyContent: "center" }}>
          <LogoBlock
            eventItem={eventItem}
            logoPath={logoPath}
            logoPadding={logoPadding}
            className={null}
          />
        </Box>
      )}

      <RegistrationUserState>
        <RegistrationEmail match={match} event={event} saveAuth={false} loading={loading} />
      </RegistrationUserState>
    </>
  );
}

const RegistrationContainer = ({ event, match, loading /* saveAuth unused */ }) => {
  const [open, setOpenDialog] = useState(true);
  const [isHeader] = useState(false);
  const eventItem = event;
  console.log('--- registration container event', eventItem);
  const zoomEnabled = Boolean(eventItem?.zoom?.enable);

  const derived = useMemo(() => {
    if (!eventItem) {
      return {
        brandingFound: false,
        logoPadding: "10px",
        logoPath: null,
        headerTitle: "",
        headerBackground: "#ffffff",
        holdingBlockLayout: getHoldingBlockLayout(null),
      };
    }

    return {
      brandingFound: computeBrandingFound(eventItem),
      logoPadding: computeLogoPadding(eventItem),
      logoPath: findWebsiteLogoPath(eventItem),
      headerTitle: computeHeaderTitle(eventItem),
      headerBackground: computeHeaderBackground(eventItem),
      // Zoom registration keeps a centered dialog — ignore holding block position
      holdingBlockLayout: zoomEnabled
        ? getHoldingBlockLayout(null)
        : getHoldingBlockLayout(eventItem?.webcast?.branding),
    };
  }, [eventItem, zoomEnabled]);

  const handleClose = useCallback((_, reason) => {
    if (reason !== "backdropClick") setOpenDialog(false);
  }, []);

  // Keep original behaviour: don’t crash if event not loaded; simply render baseline/background.
  return (
    <>
      <Background eventItem={eventItem} />
      <CssBaseline />

      <Container
        maxWidth="xl"
        className="mainContainer"
        style={zoomEnabled ? { width: "80vw" } : undefined}
      >
        <Grid item xs={12} id="firstRow" className="whiteBg">
          {/* {isHeader && eventItem && (
            <HeaderBar
            event={eventItem}
              eventId={match?.match?.params?.eventId}
              eventHeaderTitle={derived.headerTitle}
              logoPath={derived.logoPath}
              logoPadding={derived.logoPadding}
              headerBackground={derived.headerBackground}
              brandingFound={derived.brandingFound}
            />
          )} */}
        </Grid>

        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="form-dialog-title"
          disableEscapeKeyDown
          classes={{
            container: `registration-dialog-container registration-dialog-container--${derived.holdingBlockLayout.horizontal} registration-dialog-container--align-${derived.holdingBlockLayout.vertical}`,
          }}
          {...(zoomEnabled
            ? {
                TransitionComponent: Transition,
                maxWidth: false,
                fullWidth: true,
                PaperProps: {
                  style: {
                    borderRadius: "10px",
                    padding: "20px",
                    width: "min(1100px, 94vw)",
                    maxWidth: "1100px",
                    background: "linear-gradient(145deg, #ffffff, #f5faff)",
                    boxShadow: "0 14px 50px rgba(0,0,0,0.25)",
                    animation: "fadeIn 0.5s ease-out",
                  },
                },
              }
            : {})}
        >
          {/* Guard against event not yet loaded */}
          {!eventItem ? null : zoomEnabled ? (
            <ZoomDialogContent
              eventItem={eventItem}
              logoPath={derived.logoPath}
              logoPadding={derived.logoPadding}
              match={match}
              event={event}
              loading={loading}
            />
          ) : (
            <DefaultDialogContent
              eventItem={eventItem}
              logoPath={derived.logoPath}
              logoPadding={derived.logoPadding}
              match={match}
              event={event}         
              loading={loading}
            />
          )}
        </Dialog>
      </Container>
    </>
  );
};

const areEqual = (prevProps, nextProps) => prevProps.event === nextProps.event;

export default React.memo(RegistrationContainer, areEqual);
